import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandSeparator } from "@/components/ui/command";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarIcon, Search, Plus, Minus, Clock, MapPin, Users, Hotel, X, Palmtree, BookOpen } from "lucide-react";
import { format } from "date-fns";
import { es, ptBR, enUS } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { useDestinations } from "@/hooks/useDestinations";
import { useDestinationGuides } from "@/hooks/useDestinationGuides";
import { useSearchHistory } from "@/hooks/useSearchHistory";
import { RESORT_DESTINATIONS, ResortDestination } from "@/types/resort";
import type { DateRange } from "react-day-picker";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "react-i18next";
import { useLocale } from "@/contexts/LocaleContext";
import { useNavigate } from "@/lib/react-router-dom-shim";
import type { LucideIcon } from "lucide-react";

type ProductTab = {
  id: string;
  label: string;
  icon: LucideIcon;
  available: boolean;
};

export const HeroSearchForm = () => {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  const [activeTab, setActiveTab] = useState("hotels");
  const [searchTerm, setSearchTerm] = useState("");
  const { destinations, loading: loadingDestinations } = useDestinations(searchTerm);
  const { data: destinationGuides = [] } = useDestinationGuides(searchTerm);
  const { recentSearches, saveSearch } = useSearchHistory();
  const [destinationOpen, setDestinationOpen] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState<string>("");
  const [destinationName, setDestinationName] = useState<string>("");
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [guestsOpen, setGuestsOpen] = useState(false);
  const [adults, setAdults] = useState(2);
  const [childrenCount, setChildrenCount] = useState(0);
  const [childrenAges, setChildrenAges] = useState<number[]>([]);
  const [showValidation, setShowValidation] = useState(false);
  
  // Resort-specific state
  const [selectedResortDestination, setSelectedResortDestination] = useState<ResortDestination | null>(null);

  const productTabs: ProductTab[] = [
    { id: "hotels", label: t('nav.hotels'), icon: Hotel, available: true },
    { id: "resorts", label: t('nav.resorts'), icon: Palmtree, available: true },
    // Future tabs can be added here
    // { id: "cars", label: t('nav.cars'), icon: Car, available: false },
    // { id: "activities", label: t('nav.activities'), icon: Ticket, available: false },
  ];

  const getDateLocale = () => {
    switch (locale) {
      case 'pt-BR':
        return ptBR;
      case 'en':
        return enUS;
      default:
        return es;
    }
  };

  const dateLocale = getDateLocale();

  const handleChildrenCountChange = (newCount: number) => {
    if (newCount < 0) return;
    setChildrenCount(newCount);
    
    if (newCount < childrenAges.length) {
      setChildrenAges(childrenAges.slice(0, newCount));
    } else if (newCount > childrenAges.length) {
      setChildrenAges([...childrenAges, ...Array(newCount - childrenAges.length).fill(0)]);
    }
  };

  const handleChildAgeChange = (index: number, age: number) => {
    const newAges = [...childrenAges];
    newAges[index] = Math.max(0, Math.min(17, age));
    setChildrenAges(newAges);
  };

  const handleDestinationSelect = (dest: { DestinationId: string; Name: string; Type?: string }) => {
    setSelectedDestination(dest.DestinationId);
    setDestinationName(dest.Name);
    setDestinationOpen(false);
    setSearchTerm("");
    saveSearch({ 
      DestinationId: dest.DestinationId, 
      Name: dest.Name, 
      Type: dest.Type || '' 
    });
  };

  const handleSearch = () => {
    if (activeTab === "resorts") {
      // Resort search
      if (!selectedResortDestination || !dateRange?.from || !dateRange?.to) {
        setShowValidation(true);
        return;
      }

      setShowValidation(false);
      const searchParams = new URLSearchParams({
        resortDestinationId: selectedResortDestination.id,
        resortDestinationName: selectedResortDestination.name,
        checkIn: format(dateRange.from, "yyyy-MM-dd"),
        checkOut: format(dateRange.to, "yyyy-MM-dd"),
        adults: adults.toString(),
        childrenAges: childrenAges.join(","),
      });

      navigate(`/resorts?${searchParams.toString()}`);
    } else {
      // Hotel search
      if (!selectedDestination || !dateRange?.from || !dateRange?.to) {
        setShowValidation(true);
        return;
      }

      setShowValidation(false);
      const searchParams = new URLSearchParams({
        destinationId: selectedDestination,
        destinationName: destinationName,
        checkIn: format(dateRange.from, "yyyy-MM-dd"),
        checkOut: format(dateRange.to, "yyyy-MM-dd"),
        adults: adults.toString(),
        childrenAges: childrenAges.join(","),
      });

      navigate(`/hoteles?${searchParams.toString()}`);
    }
  };

  const isDestinationInvalid = showValidation && (
    activeTab === "hotels" ? !selectedDestination : !selectedResortDestination
  );
  const isDatesInvalid = showValidation && (!dateRange?.from || !dateRange?.to);

  const guestSummary = () => {
    const adultText = adults === 1 
      ? `1 ${t('common.adult')}` 
      : `${adults} ${t('common.adults')}`;
    
    if (childrenCount === 0) {
      return adultText;
    }
    
    const childText = childrenCount === 1 
      ? `1 ${t('common.child')}` 
      : `${childrenCount} ${t('common.children')}`;
    
    return `${adultText}, ${childText}`;
  };

  return (
    <div className="w-full max-w-5xl mx-auto">
      {/* Product Tabs */}
      <div className="flex items-center gap-2 mb-4">
        {productTabs.map((tab) => {
          const IconComponent = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => {
                if (tab.available) {
                  setActiveTab(tab.id);
                  setShowValidation(false);
                }
              }}
              disabled={!tab.available}
              className={cn(
                "flex items-center gap-2 px-5 py-3 rounded-t-lg text-sm font-semibold transition-all",
                activeTab === tab.id
                  ? "bg-card text-foreground shadow-lg"
                  : tab.available
                    ? "bg-card/20 text-primary-foreground hover:bg-card/40"
                    : "bg-card/10 text-primary-foreground/50 cursor-not-allowed"
              )}
            >
              <IconComponent className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Search Form */}
      <form role="search" aria-label={activeTab === "resorts" ? "Buscar resorts" : "Buscar hoteles"} onSubmit={(e) => e.preventDefault()} className="bg-card rounded-lg rounded-tl-none shadow-xl p-4 md:p-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {/* Destination - Different based on active tab */}
          <div className="md:col-span-4">
            <label className={cn(
              "text-xs font-medium mb-1.5 block",
              isDestinationInvalid ? "text-destructive" : "text-muted-foreground"
            )}>
              {activeTab === "resorts" ? t('resorts.selectDestination') : t('search.destination')} {isDestinationInvalid && "*"}
            </label>
            
            {activeTab === "resorts" ? (
              /* Resort Destination Select */
              <Select
                value={selectedResortDestination?.id || ""}
                onValueChange={(value) => {
                  const dest = RESORT_DESTINATIONS.find(d => d.id === value);
                  setSelectedResortDestination(dest || null);
                  setShowValidation(false);
                }}
              >
                <SelectTrigger className={cn(
                  "w-full h-12 bg-background text-left font-normal text-foreground",
                  isDestinationInvalid ? "border-destructive ring-1 ring-destructive" : "border-border",
                  !selectedResortDestination && "text-muted-foreground"
                )}>
                  <div className="flex items-center gap-3 min-w-0">
                    <Palmtree className={cn("h-5 w-5 shrink-0", isDestinationInvalid ? "text-destructive" : "text-primary")} />
                    <span className={cn("truncate", !selectedResortDestination && "text-muted-foreground")}>
                      <SelectValue placeholder={t('resorts.chooseDestination')} />
                    </span>
                  </div>
                </SelectTrigger>
                <SelectContent className="bg-popover z-[9999]">
                  {RESORT_DESTINATIONS.map((dest) => (
                    <SelectItem key={dest.id} value={dest.id} className="cursor-pointer">
                      {t(dest.nameKey) || dest.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              /* Hotel Destination Autocomplete */
              <Popover open={destinationOpen} onOpenChange={(open) => {
                setDestinationOpen(open);
                if (open) setShowValidation(false);
              }}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={destinationOpen}
                    aria-label={t('search.destinationPlaceholder')}
                    className={cn(
                      "w-full h-12 justify-start gap-3 bg-background text-left font-normal",
                      isDestinationInvalid ? "border-destructive ring-1 ring-destructive" : "border-border"
                    )}
                  >
                    <MapPin className={cn("h-5 w-5 shrink-0", isDestinationInvalid ? "text-destructive" : "text-primary")} />
                    <span className={cn(
                      "truncate",
                      !destinationName && "text-muted-foreground"
                    )}>
                      {destinationName || t('search.destinationPlaceholder')}
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent 
                  className="w-[var(--radix-popover-trigger-width)] sm:w-[400px] p-0 bg-popover border border-border shadow-xl z-[9999]" 
                  align="start"
                  side="bottom"
                  sideOffset={4}
                >
                  <Command shouldFilter={false} className="bg-popover">
                    <CommandInput 
                      placeholder={t('search.searchDestinationMin')}
                      value={searchTerm}
                      onValueChange={setSearchTerm}
                      className="bg-background border-b border-border"
                    />
                    {loadingDestinations && (
                      <div className="py-6 text-center text-sm text-muted-foreground">
                        {t('search.searchingDestinations')}
                      </div>
                    )}
                    {!loadingDestinations && (
                      <>
                        {searchTerm.length === 0 && recentSearches.length > 0 && (
                          <>
                            <CommandGroup heading={t('search.recentSearches')} className="max-h-[300px] overflow-auto">
                              {recentSearches.map((dest) => (
                                <CommandItem
                                  key={`recent-${dest.DestinationId}`}
                                  value={dest.Name}
                                  onSelect={() => handleDestinationSelect(dest)}
                                  className="cursor-pointer px-4 py-2 text-foreground hover:bg-accent"
                                >
                                  <Clock className="mr-2 h-4 w-4 text-muted-foreground" />
                                  {dest.Name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                            <CommandSeparator />
                          </>
                        )}
                        <CommandEmpty className="py-6 text-center text-sm text-muted-foreground">
                          {searchTerm.length < 3 
                            ? t('search.typeMinLetters')
                            : t('search.noDestinationsFound')}
                        </CommandEmpty>
                        {searchTerm.length >= 3 && destinationGuides.length > 0 && (
                          <>
                            <CommandGroup heading={t('search.destinationGuides') || 'Guias de destino'}>
                              {destinationGuides.map((g) => (
                                <CommandItem
                                  key={`guide-${g.slug}`}
                                  value={`guide-${g.name}`}
                                  onSelect={() => {
                                    setDestinationOpen(false);
                                    navigate(`/destino/${g.slug}`);
                                  }}
                                  className="cursor-pointer px-4 py-2 text-foreground hover:bg-accent"
                                >
                                  <BookOpen className="mr-2 h-4 w-4 text-primary" />
                                  <span className="flex-1">
                                    {g.name}
                                    {g.state ? <span className="text-muted-foreground">, {g.state}</span> : null}
                                  </span>
                                  <span className="ml-2 text-xs text-primary">
                                    {t('search.viewGuide') || 'Ver guia'}
                                  </span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                            <CommandSeparator />
                          </>
                        )}
                        {searchTerm.length >= 3 && (
                          <CommandGroup className="max-h-[300px] overflow-auto">
                            {destinations.map((dest) => (
                              <CommandItem
                                key={`${dest.DestinationId}-${dest.Name}`}
                                value={dest.Name}
                                onSelect={() => handleDestinationSelect(dest)}
                                className="cursor-pointer px-4 py-2 text-foreground hover:bg-accent"
                              >
                                {dest.Name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}
                      </>
                    )}
                  </Command>
                </PopoverContent>
              </Popover>
            )}
          </div>

          {/* Dates */}
          <div className="md:col-span-4">
            <label className={cn(
              "text-xs font-medium mb-1.5 block",
              isDatesInvalid ? "text-destructive" : "text-muted-foreground"
            )}>
              {t('search.dates')} {isDatesInvalid && "*"}
            </label>
            <Popover open={datePickerOpen} onOpenChange={(open) => {
              setDatePickerOpen(open);
              if (open) setShowValidation(false);
            }}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  aria-label={t('search.selectDates')}
                  className={cn(
                    "w-full h-12 justify-start gap-3 bg-background text-left font-normal",
                    isDatesInvalid ? "border-destructive ring-1 ring-destructive" : "border-border",
                    (!dateRange || !dateRange.from) && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className={cn("h-5 w-5 shrink-0", isDatesInvalid ? "text-destructive" : "text-primary")} />
                  {dateRange && dateRange.from ? (
                    dateRange.to ? (
                      <span className="truncate">
                        {format(dateRange.from, "dd MMM", { locale: dateLocale })} - {format(dateRange.to, "dd MMM yyyy", { locale: dateLocale })}
                      </span>
                    ) : (
                      format(dateRange.from, "dd MMM yyyy", { locale: dateLocale })
                    )
                  ) : (
                    <span>{t('search.selectDates')}</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent 
                className="w-auto p-0 bg-popover z-[9999]" 
                align={isMobile ? "center" : "start"}
              >
                <div className="flex flex-col">
                  <Calendar
                    mode="range"
                    selected={dateRange}
                    onSelect={(range) => {
                      if (range?.from && range?.to && 
                          range.from.getTime() === range.to.getTime()) {
                        return;
                      }
                      setDateRange(range);
                      if (range?.from && range?.to) {
                        setDatePickerOpen(false);
                      }
                    }}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      
                      if (date < today) {
                        return true;
                      }
                      
                      if (dateRange?.from) {
                        const checkInDate = new Date(dateRange.from);
                        checkInDate.setHours(0, 0, 0, 0);
                        const currentDate = new Date(date);
                        currentDate.setHours(0, 0, 0, 0);
                        
                        if (currentDate.getTime() === checkInDate.getTime()) {
                          return true;
                        }
                      }
                      
                      return false;
                    }}
                    initialFocus
                    numberOfMonths={isMobile ? 1 : 2}
                    locale={dateLocale}
                    className="pointer-events-auto"
                  />
                  {dateRange?.from && (
                    <div className="border-t p-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-muted-foreground hover:text-foreground"
                        onClick={() => setDateRange(undefined)}
                      >
                        <X className="h-4 w-4 mr-2" />
                        {t('common.clearDates')}
                      </Button>
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Guests */}
          <div className="md:col-span-3">
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
              {t('search.guests')}
            </label>
            <Popover open={guestsOpen} onOpenChange={setGuestsOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full h-12 justify-start gap-3 bg-background border-border text-left font-normal"
                >
                  <Users className="h-5 w-5 text-primary shrink-0" />
                  <span className="truncate">{guestSummary()}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-4 bg-popover z-[9999]" align="start">
                <div className="space-y-4">
                  {/* Adults */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{t('search.adults')}</p>
                      <p className="text-sm text-muted-foreground">18+</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setAdults(Math.max(1, adults - 1))}
                        disabled={adults <= 1}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="w-8 text-center font-medium">{adults}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setAdults(adults + 1)}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Children */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{t('search.children')}</p>
                      <p className="text-sm text-muted-foreground">0-17</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleChildrenCountChange(childrenCount - 1)}
                        disabled={childrenCount <= 0}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="w-8 text-center font-medium">{childrenCount}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleChildrenCountChange(childrenCount + 1)}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Children Ages */}
                  {childrenCount > 0 && (
                    <div className="pt-4 border-t space-y-3">
                      <p className="text-sm font-medium">{t('search.childrenAge')}</p>
                      <div className="grid grid-cols-2 gap-3">
                        {Array.from({ length: childrenCount }).map((_, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground shrink-0">
                              {t('search.childLabel', { number: index + 1 })}:
                            </span>
                            <div className="flex items-center gap-1">
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => handleChildAgeChange(index, Math.max(0, (childrenAges[index] || 0) - 1))}
                              >
                                <Minus className="h-3 w-3" />
                              </Button>
                              <span className="w-6 text-center text-sm">{childrenAges[index] || 0}</span>
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => handleChildAgeChange(index, Math.min(17, (childrenAges[index] || 0) + 1))}
                              >
                                <Plus className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Search Button */}
          <div className="md:col-span-1 flex items-end">
            <Button 
              onClick={handleSearch} 
              className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground"
              disabled={activeTab === "resorts" 
                ? (!selectedResortDestination || !dateRange?.from || !dateRange?.to)
                : (!selectedDestination || !dateRange?.from || !dateRange?.to)
              }
            >
              <Search className="h-5 w-5" />
              <span className="sr-only">{activeTab === "resorts" ? t('resorts.searching') : t('search.searchHotels')}</span>
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};
